import type { UsersFilterState } from './components/UsersFilterBar';
import { ROLE_BY_ID, STATUS_FILTER_ORDER } from './roleTaxonomy';
import type { UserRoleId, UserStatus } from './roleTaxonomy';

const PARAM = {
  search: 'q',
  role: 'role',
  region: 'region',
  status: 'status',
  showInactive: 'inactive',
} as const;

export const DEFAULT_USERS_FILTERS: UsersFilterState = {
  search: '',
  role: 'any',
  status: 'any',
  regionId: 'any',
  showInactive: false,
};

function readRole(value: string | null): UserRoleId | 'any' {
  return value && value in ROLE_BY_ID ? (value as UserRoleId) : 'any';
}

function readStatus(value: string | null): UserStatus | 'any' {
  return STATUS_FILTER_ORDER.find((s) => s === value) ?? 'any';
}

function readRegion(value: string | null, allowedRegionIds: readonly string[]): string | 'any' {
  return value && allowedRegionIds.includes(value) ? value : 'any';
}

/** Reads the Users list filters from the page address. Anything missing or not
 *  recognised falls back to its default, and a region outside
 *  `allowedRegionIds` is ignored. */
export function readUsersFilters(
  params: URLSearchParams,
  allowedRegionIds: readonly string[],
): UsersFilterState {
  return {
    search: params.get(PARAM.search) ?? '',
    role: readRole(params.get(PARAM.role)),
    status: readStatus(params.get(PARAM.status)),
    regionId: readRegion(params.get(PARAM.region), allowedRegionIds),
    showInactive: params.get(PARAM.showInactive) === '1',
  };
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null) {
  if (value) params.set(key, value);
  else params.delete(key);
}

/** Returns a copy of `params` holding `filters`, leaving out any filter at its
 *  default and keeping unrelated parameters as they were. */
export function writeUsersFilters(
  params: URLSearchParams,
  filters: UsersFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  setOrDelete(next, PARAM.search, filters.search);
  setOrDelete(next, PARAM.role, filters.role === 'any' ? null : filters.role);
  setOrDelete(next, PARAM.status, filters.status === 'any' ? null : filters.status);
  setOrDelete(next, PARAM.region, filters.regionId === 'any' ? null : filters.regionId);
  setOrDelete(next, PARAM.showInactive, filters.showInactive ? '1' : null);
  return next;
}
